import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Play, Plus, X, ExternalLink, Video } from 'lucide-react';
import { toast } from 'sonner';
import { teamService } from '@/services/teamService';
import VideoPlayer from '@/components/media/VideoPlayer';
import { supabase } from '@/integrations/supabase/client';

interface TeamVideo {
  id: string;
  team_id: number;
  video_url: string;
  video_title?: string;
  video_description?: string;
  display_order: number;
  created_at: string;
}

interface TeamVideoGalleryProps {
  teamId: number;
  canManage: boolean;
}

export const TeamVideoGallery = ({ teamId, canManage }: TeamVideoGalleryProps) => {
  const [videos, setVideos] = useState<TeamVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [newVideoDescription, setNewVideoDescription] = useState('');
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const data = await teamService.getTeamVideos(teamId);
      setVideos(data);
    } catch (error) {
      console.error('Error loading team videos:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, [teamId]);

  const addVideo = async () => {
    if (!newVideoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }

    try {
      // Get current user ID for uploaded_by field - prioritize internal user
      let currentUserId: string | null = null;
      
      // First check internal_user_session which is where useInternalAuth stores the user
      try {
        const sessionData = localStorage.getItem('internal_user_session');
        if (sessionData) {
          console.log('Found internal_user_session:', sessionData);
          const parsed = JSON.parse(sessionData);
          if (parsed && parsed.user && parsed.user.id) {
            currentUserId = parsed.user.id;
            console.log('Using internal user ID from session for video upload:', currentUserId);
          }
        }
      } catch (e) {
        console.log('Error parsing internal_user_session:', e);
      }
      
      // If no internal user session, try Supabase auth
      if (!currentUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        currentUserId = user?.id || null;
        if (currentUserId) {
          console.log('Using Supabase user ID for video upload:', currentUserId);
        }
      }

      if (!currentUserId) {
        toast.error('You must be logged in to add videos');
        return;
      }

      // Insert video directly into database
      const { error } = await supabase
        .from('team_videos')
        .insert({
          team_id: teamId,
          video_url: newVideoUrl,
          video_title: newVideoTitle || null,
          video_description: newVideoDescription || null,
          uploaded_by: currentUserId,
          display_order: videos.length + 1
        });

      if (error) {
        console.error('Database error:', error);
        toast.error(`Failed to add video: ${error.message}`);
        return;
      }

      toast.success('Video added successfully');
      setNewVideoUrl('');
      setNewVideoTitle('');
      setNewVideoDescription('');
      setShowAddModal(false);
      loadVideos();
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error('Failed to add video');
    }
  };

  const removeVideo = async (videoId: string) => {
    try {
      const { error } = await supabase
        .from('team_videos')
        .delete()
        .eq('id', videoId);

      if (error) {
        console.error('Database error:', error);
        toast.error(`Failed to remove video: ${error.message}`);
        return;
      }

      toast.success('Video removed successfully');
      loadVideos();
    } catch (error) {
      console.error('Error removing video:', error);
      toast.error('Failed to remove video');
    }
  };

  const getVideoThumbnail = (url: string) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      return `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`;
    }
    
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
    }
    
    return '/placeholder.svg';
  };

  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Video className="h-5 w-5" />
            Team Videos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="aspect-video bg-gray-700 rounded-lg mb-4"></div>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-video bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentVideo = videos[currentVideoIndex];

  return (
    <>
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Team Videos ({videos.length}/10)
            </div>
            {canManage && videos.length < 10 && (
              <Button
                onClick={() => setShowAddModal(true)}
                size="sm"
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Video
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {videos.length > 0 ? (
            <div className="space-y-4">
              {/* Main Video Player */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                    <VideoPlayer
                      url={currentVideo.video_url}
                      title={currentVideo.video_title}
                      className="w-full aspect-video"
                    />
                    
                    {/* Video Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-4">
                      <div className="text-white">
                        {currentVideo.video_title && (
                          <h3 className="font-semibold mb-1">{currentVideo.video_title}</h3>
                        )}
                        {currentVideo.video_description && (
                          <p className="text-sm text-gray-300 line-clamp-2">{currentVideo.video_description}</p>
                        )}
                      </div>
                    </div>

                    {/* Remove button for main video */}
                    {canManage && (
                      <div className="absolute top-2 right-2">
                        <Button
                          onClick={() => removeVideo(currentVideo.id)}
                          size="sm"
                          variant="destructive"
                          className="p-1 h-auto"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Thumbnails */}
                <div className="space-y-2">
                  <h4 className="text-white font-medium text-sm">More Videos</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {videos.map((video, index) => (
                      <div
                        key={video.id}
                        className={`relative group cursor-pointer rounded overflow-hidden transition-all ${
                          index === currentVideoIndex 
                            ? 'ring-2 ring-sports-green' 
                            : 'hover:ring-1 hover:ring-gray-500'
                        }`}
                        onClick={() => setCurrentVideoIndex(index)}
                      >
                        <div className="aspect-video bg-gray-700">
                          <img
                            src={getVideoThumbnail(video.video_url)}
                            alt={video.video_title || 'Team video'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder.svg';
                            }}
                          />
                          
                          {/* Play overlay */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-opacity">
                            <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center border border-white">
                              <Play className="h-4 w-4 text-white ml-0.5" />
                            </div>
                          </div>

                          {/* Video controls styling */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                            <div className="flex items-center justify-between text-white text-xs">
                              <div className="flex items-center space-x-1">
                                <div className="w-12 h-0.5 bg-white bg-opacity-30 rounded">
                                  <div className="w-1/3 h-full bg-red-600 rounded"></div>
                                </div>
                                <span>00:40/00:42</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Video title */}
                        {video.video_title && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 p-1">
                            <p className="text-white text-xs truncate">{video.video_title}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No videos added yet.</p>
              {canManage && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 bg-sports-green text-black hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Video
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Video Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Add Team Video</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="video-url" className="text-white">Video URL *</Label>
              <Input
                id="video-url"
                value={newVideoUrl}
                onChange={(e) => setNewVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="video-title" className="text-white">Title (optional)</Label>
              <Input
                id="video-title"
                value={newVideoTitle}
                onChange={(e) => setNewVideoTitle(e.target.value)}
                placeholder="Video title..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="video-description" className="text-white">Description (optional)</Label>
              <Textarea
                id="video-description"
                value={newVideoDescription}
                onChange={(e) => setNewVideoDescription(e.target.value)}
                placeholder="Describe this video..."
                className="bg-gray-700 border-gray-600 text-white"
                rows={3}
              />
            </div>
            
            {newVideoUrl && (
              <div>
                <Label className="text-white">Preview</Label>
                <div className="aspect-video bg-gray-700 rounded-lg overflow-hidden mt-2">
                  <img
                    src={getVideoThumbnail(newVideoUrl)}
                    alt="Video preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder.svg';
                    }}
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setShowAddModal(false)}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={addVideo}
                disabled={!newVideoUrl.trim()}
                className="bg-sports-green text-black hover:bg-green-600 disabled:bg-gray-600 disabled:text-gray-400"
              >
                Add Video
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};